import React from 'react';
import { Link } from 'react-router-dom';
import {
  Shield,
  Ban,
  Heart,
  Crown,
  Lock,
} from 'lucide-react';
import HowItWorks from '../components/home/HowItWorks';
import SampleProfiles from '../components/home/SampleProfiles';
import WhyPremium from '../components/home/WhyPremium';
import PricingSection from '../components/home/PricingSection';
import FAQSection from '../components/home/FAQSection';

// Hero profile grid: 5 locked (full blur) + 1 unlocked (semi blur). Replace with local photos in public/profile-samples/ (hero-1.jpg … hero-6.jpg).
const heroPhotoFallbacks = [
  'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=533&fit=crop',
  'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400&h=533&fit=crop',
  'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?w=400&h=533&fit=crop',
  'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=400&h=533&fit=crop',
  'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400&h=533&fit=crop',
  'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=400&h=533&fit=crop',
];

const heroProfiles = [
  { name: 'Priya (24, Delhi)', tags: 'Friendly | Creative', blurred: true, photo: heroPhotoFallbacks[0], photoLocal: '/profile-samples/hero-1.jpg' },
  { name: 'Rahul (28, Mumbai)', tags: 'Chill | Gaming', blurred: true, photo: heroPhotoFallbacks[1], photoLocal: '/profile-samples/hero-2.jpg' },
  { name: 'Kavya (26, Chennai)', tags: 'Travel | Music', blurred: true, photo: heroPhotoFallbacks[2], photoLocal: '/profile-samples/hero-3.jpg' },
  { name: 'Riya (25, Mumbai)', tags: 'Friendly | Talkative | Traveler', verified: true, available: true, photo: heroPhotoFallbacks[3], photoLocal: '/profile-samples/hero-4.jpg' },
  { name: 'Neha (27, Hyderabad)', tags: 'Art | Coffee', blurred: true, photo: heroPhotoFallbacks[4], photoLocal: '/profile-samples/hero-5.jpg' },
  { name: 'Sara (22, Pune)', tags: 'Movies | Fitness', blurred: true, photo: heroPhotoFallbacks[5], photoLocal: '/profile-samples/hero-6.jpg' },
];

const HomePage = () => {
  return (
    <div>
      {/* Hero – dark theme, original design */}
      <section className="relative overflow-hidden bg-slate-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: tagline, bullets, CTAs */}
            <div>
              <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">
                Find a verified friend for chats, emotional support, or fun—anytime, anywhere.
              </h1>
              <ul className="mt-6 space-y-4 text-white/90">
                <li className="flex items-center gap-3">
                  <span className="flex items-center justify-center w-9 h-9 rounded-full bg-white/10 shrink-0">
                    <Shield className="h-5 w-5 text-brand-pink" />
                  </span>
                  <span>100% Verified Profiles</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex items-center justify-center w-9 h-9 rounded-full bg-white/10 shrink-0">
                    <Ban className="h-5 w-5 text-red-400" />
                  </span>
                  <span>No Free Users = No Junk</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex items-center justify-center w-9 h-9 rounded-full bg-white/10 shrink-0">
                    <Heart className="h-5 w-5 text-brand-pink" />
                  </span>
                  <span>Real People. Real Connections.</span>
                </li>
              </ul>
              <div className="mt-8 flex flex-col items-start gap-3">
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-brand-pink text-white font-semibold hover:bg-brand-pink-dark transition-colors shadow-lg shadow-brand-pink/25"
                >
                  <Crown className="h-5 w-5" />
                  JOIN NOW – ₹1000 (MONTHLY)
                </Link>
                <Link
                  to="/how-it-works"
                  className="text-white/80 hover:text-white font-medium transition-colors underline underline-offset-2"
                >
                  KNOW MORE
                </Link>
              </div>
            </div>

            {/* Right: 6-card profile grid */}
            <div className="relative">
              <div className="grid grid-cols-3 gap-3 sm:gap-4">
                {heroProfiles.map((p, i) => (
                  <div
                    key={i}
                    className={`rounded-xl overflow-hidden border ${
                      p.verified
                        ? 'bg-slate-700/80 border-brand-pink/40 ring-1 ring-brand-pink/30'
                        : 'bg-slate-800/90 border-slate-600/50'
                    }`}
                  >
                    <div className="aspect-[3/4] relative flex items-center justify-center bg-slate-800 overflow-hidden">
                      <img
                        src={p.photo}
                        alt=""
                        onError={(e) => { e.target.onerror = null; e.target.src = p.photo; }}
                        className={`absolute inset-0 w-full h-full object-cover ${p.blurred ? 'blur-[20px] scale-110' : 'blur-[6px]'}`}
                      />
                      {p.blurred ? (
                        <>
                          <div className="absolute inset-0 bg-slate-900/40" />
                          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-2 z-10">
                            <Lock className="h-5 w-5 text-brand-pink drop-shadow" />
                          </div>
                        </>
                      ) : null}
                      {p.verified && (
                        <span className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-brand-pink text-white">
                          Verified
                        </span>
                      )}
                    </div>
                    <div className="p-2.5 text-center">
                      <p className="text-xs font-medium text-white truncate">{p.name}</p>
                      <p className="text-[10px] text-slate-400 truncate mt-0.5">{p.tags}</p>
                      {p.available && (
                        <span className="inline-block mt-1.5 px-2 py-0.5 rounded text-[10px] font-medium bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                          Available
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-4 flex items-center justify-center gap-2 text-slate-400 text-sm">
                <Lock className="h-4 w-4 text-amber-400/90" />
                Only premium users can view full profile & chat
              </p>
            </div>
          </div>
        </div>

        {/* Stats bar */}
        <div className="border-t border-white/10 bg-slate-800/80">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-wrap items-center justify-center gap-10 sm:gap-16">
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-amber-500/20 text-amber-400">
                  <Crown className="h-5 w-5" />
                </span>
                <span className="text-white/90 font-medium">
                  <span className="text-amber-400 font-semibold">0</span> verified friends
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-amber-500/20 text-amber-400">
                  <Crown className="h-5 w-5" />
                </span>
                <span className="text-white/90 font-medium">
                  <span className="text-amber-400 font-semibold">0</span> paid users
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <HowItWorks />
      <SampleProfiles />
      <WhyPremium />
      <PricingSection />
      <FAQSection />
    </div>
  );
};

export default HomePage;
